import { useCallback, useEffect, useMemo } from 'react'

import {
  QUERY_GET_UNIT_ACTIVATION_BY_ID,
  QUERY_SEARCH_UNIT_ACTIVATIONS,
  QUERY_SEARCH_UNIT_ACTIVATIONS_INFINITE
} from '@apps-common/apis/portal/unit-activations'
import { getUnitActivationById } from '@apps-common/apis/portal/unit-activations/get-unit-activation-by-id'
import { UNIT_ACTIVATION_UPDATE_LISTENER_STATUSES } from '@apps-common/config/unit-activation'
import { useUnitActivationContext } from '@apps-common/contexts'
import { queryClient } from '@apps-common/lib'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'

export const useActivationUpdates = (
  containerId: string,
  tasksLoading: boolean,
  allLoading: boolean,
  tasks: IActivationServiceRequestModel[]
) => {
  useEffect(() => {
    const element = document.getElementById(containerId)

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [tasksLoading])

  const getActivationId = (value: IActivationServiceRequestModel | number | string | undefined | null) => {
    if (value === null || value === undefined) {
      return undefined
    }

    const raw = typeof value === 'object' ? value.unitActivationRequestId : value
    const id = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
    return Number.isNaN(id) ? undefined : id
  }

  const updateActivationInCache = useCallback((updated: IActivationServiceRequestModel) => {
    const updatedId = getActivationId(updated)

    if (!updatedId) {
      return
    }

    queryClient.setQueryData(QUERY_GET_UNIT_ACTIVATION_BY_ID(updatedId), updated)

    const updateList = (data: IActivationServiceRequestModel[] | undefined) => {
      if (!Array.isArray(data)) {
        return data
      }

      const index = data.findIndex((item) => getActivationId(item) === updatedId)
      if (index === -1) {
        return data
      }

      return data.map((item) => (getActivationId(item) === updatedId ? updated : item))
    }

    queryClient.setQueriesData({ queryKey: QUERY_SEARCH_UNIT_ACTIVATIONS() }, updateList)

    queryClient.setQueriesData({ queryKey: QUERY_SEARCH_UNIT_ACTIVATIONS_INFINITE() }, (data: any) => {
      if (!data || !Array.isArray(data.pages)) {
        return data
      }

      const pages = data.pages.map((page: IActivationServiceRequestModel[]) => updateList(page))
      return { ...data, pages }
    })
  }, [])

  const refreshCb = useCallback(
    async (activationId: number) => {
      try {
        const updated = await queryClient.fetchQuery({
          queryKey: QUERY_GET_UNIT_ACTIVATION_BY_ID(activationId),
          queryFn: () => getUnitActivationById(activationId)
        })

        if (updated) {
          updateActivationInCache(updated)
        }
      } catch (error) {
        queryClient.refetchQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_BY_ID(activationId) })
      }
    },
    [updateActivationInCache]
  )

  const activationContext = useUnitActivationContext()
  const pendingTasks = useMemo(() => {
    if (tasksLoading || allLoading) {
      return []
    }

    return tasks?.filter((q) => UNIT_ACTIVATION_UPDATE_LISTENER_STATUSES.includes(q.status)) || []
  }, [allLoading, tasks, tasksLoading])

  useEffect(() => {
    if (!activationContext || pendingTasks.length === 0) {
      return
    }

    pendingTasks.forEach((task) => {
      activationContext.subscribe({
        request: task,
        callback: (updated) => {
          const updatedId = getActivationId(updated)
          updateActivationInCache(updated)
          console.log('[notif-update] activation refresh', updatedId)
          if (updatedId) {
            refreshCb(updatedId)
          }
        },
        refresh: () => {
          const taskId = getActivationId(task)
          console.log('[notif-update] activation refresh', taskId)
          if (taskId) {
            refreshCb(taskId)
          }
        }
      })
    })

    return () => {
      pendingTasks.forEach((task) => {
        const taskId = getActivationId(task)
        if (taskId) {
          activationContext.unsubscribe(taskId)
        }
      })
    }
  }, [activationContext, pendingTasks, refreshCb, updateActivationInCache])
}
